/**
 * Custom hooks for the new sportsmain API
 * Drop-in replacements for useSportsMarkets and useMatchOdds
 */

import { useQuery } from '@tanstack/react-query';
import { fetchAllSportsData, fetchSportsDataById } from '@/services/sports/sportsMainApiService';
import { transformListResponse, transformDetailResponse } from '@/services/sports/sportsTransformer';

// Matches to skip — test/placeholder entries from the backend
const SKIP_COMPETITION_NAMES = ['indian premier league', 'testing match'];
const SKIP_TEAM_KEYWORDS = ['test a', 'test b'];

const isSkippedMatch = (match) => {
  const comp = (match.competitionName || match.cname || '').toLowerCase().trim();
  const title = (match.matchTitle || match.ename || '').toLowerCase().trim();
  const t1 = (match.team1 || '').toLowerCase();
  const t2 = (match.team2 || '').toLowerCase();

  // Skip any placeholder entry that has no second team (ename had no "v" / "-" separator)
  if (!t2) return true;

  // Skip league-level placeholder entries (ename ≈ cname, no real teams)
  // but allow real matches that belong to the competition
  if (SKIP_COMPETITION_NAMES.some((n) => comp.includes(n))) {
    // Only skip if it's a placeholder (title matches competition name)
    if (comp === title) return true;
    // Real match with both teams — allow it through
    return false;
  }
  if (SKIP_TEAM_KEYWORDS.some((k) => t1.includes(k) || t2.includes(k))) return true;
  if (SKIP_TEAM_KEYWORDS.some((k) => title.includes(k))) return true;
  return false;
};

/**
 * Fetch all matches for a sport via the new API.
 * Drop-in replacement for useSportsMarkets.
 *
 * @param {number|null} sid - Sport ID (numeric)
 * @param {string} upperTab - 'in-play' | 'today' | 'tomorrow'
 * @returns {Object} React Query result with matches data
 */
export const useSportsMainMarkets = (sid, upperTab = 'in-play') => {
  return useQuery({
    queryKey: ['sportsMainMarkets', sid, upperTab],
    queryFn: async () => {
      if (!sid) return [];

      const apiResponse = await fetchAllSportsData(sid);
      if (!apiResponse) return [];

      const matches = transformListResponse(apiResponse, sid).filter((m) => !isSkippedMatch(m));

      // Client-side filtering by tab
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrowStart = new Date(todayStart);
      tomorrowStart.setDate(tomorrowStart.getDate() + 1);
      const dayAfterTomorrow = new Date(tomorrowStart);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

      let filtered;
      if (upperTab === 'in-play') {
        // Show in-play matches first, then upcoming
        filtered = matches;
        // Sort: in-play first, then by start time
        filtered.sort((a, b) => {
          if (a.isLive && !b.isLive) return -1;
          if (!a.isLive && b.isLive) return 1;
          return new Date(a.date) - new Date(b.date);
        });
      } else if (upperTab === 'today') {
        filtered = matches.filter((m) => {
          const matchDate = new Date(m.date);
          return matchDate >= todayStart && matchDate < tomorrowStart;
        });
      } else if (upperTab === 'tomorrow') {
        filtered = matches.filter((m) => {
          const matchDate = new Date(m.date);
          return matchDate >= tomorrowStart && matchDate < dayAfterTomorrow;
        });
      } else {
        filtered = matches;
      }

      return filtered;
    },
    enabled: !!sid,
    staleTime: upperTab === 'in-play' ? 15 * 1000 : 60 * 1000,
    refetchInterval: upperTab === 'in-play' ? 30 * 1000 : 120 * 1000,
    refetchIntervalInBackground: false,
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Fetch detailed match odds via the new API.
 * Drop-in replacement for useMatchOdds.
 *
 * @param {number|string} gmid - Game/Match ID
 * @param {number} sid - Sport ID
 * @param {Object} matchData - Match data with team names
 * @param {boolean} isMobile - Whether mobile view
 * @returns {Object} React Query result with [{ team, outcome, backOdds, layOdds }]
 */
export const useSportsMainMatchOdds = (gmid, sid, matchData, isMobile = false) => {
  return useQuery({
    queryKey: ['sportsMainMatchOdds', gmid, sid, isMobile],
    queryFn: async () => {
      if (!gmid || !sid) return null;

      const apiResponse = await fetchSportsDataById(sid, gmid);
      if (!apiResponse) return null;

      return transformDetailResponse(apiResponse, matchData, isMobile);
    },
    enabled: !!gmid && !!sid && !!matchData,
    staleTime: 3 * 1000,
    refetchInterval: 5 * 1000,
    refetchIntervalInBackground: false,
    retry: 2,
    retryDelay: 1000,
  });
};
